/**
 * @typedef {Object<string, Object<string, Array<VisitorDocumentListItem>>>} VisitorDocumentList
 * VisitorDocumentList example:
 * {
 *   "data": {
 *     "5": {
 *       "doc_type_1": [
 *         {
 *           "meta": {
 *             "checked": 0,
 *             "date_of_expire": "2100-01-01",
 *             "uuid": "00000000-0000-0000-0000-000000000000.jpeg"
 *           },
 *           "mimeType": "image/jpeg",
 *           "src": "/ru/demo/get-file/visitor-document?personalDocId=6",
 *           "thumb": "/ru/demo/get-file/visitor-document?personalDocId=6",
 *           "title": "Тип документа 1"
 *         },
 *         {
 *           "meta": {
 *             "checked": 1,
 *             "date_of_expire": "2100-01-01",
 *             "uuid": "00000000-0000-0000-0000-000000000001.pdf"
 *           },
 *           "mimeType": "application/pdf",
 *           "src": "/ru/demo/get-file/visitor-document?personalDocId=7",
 *           "thumb": "/ru/demo/img/pdf_splash.svg",
 *           "title": "Тип документа 1"
 *         }
 *       ],
 *       "doc_type_2": [
 *         {
 *           "meta": {
 *             "checked": 1,
 *             "date_of_expire": "2100-01-01",
 *             "uuid": "00000000-0000-0000-0000-000000000003.pdf"
 *           },
 *           "mimeType": "application/pdf",
 *           "src": "/ru/demo/get-file/visitor-document?personalDocId=8",
 *           "thumb": "/ru/demo/img/pdf_splash.svg",
 *           "title": "Тип документа 2"
 *         }
 *       ]
 *     },
 *     "6": {
 *       "doc_type_1": [
 *         {
 *           "meta": {
 *             "checked": 1,
 *             "date_of_expire": "2100-01-01",
 *             "uuid": "00000000-0000-0000-0000-000000000003.pdf"
 *           },
 *           "mimeType": "application/pdf",
 *           "src": "/ru/demo/get-file/visitor-document?personalDocId=9",
 *           "thumb": "/ru/demo/img/pdf_splash.svg",
 *           "title": "Тип документа 2"
 *         }
 *       ]
 *     }
 *   }
 * }
 */

import VisitorDocumentListItem from '@/common/classes/VisitorDocumentList/VisitorDocumentListItem'
import KeyDto from '@/common/classes/VisitorDocumentList/KeyDto'
import KeyItemPairDto from '@/common/classes/VisitorDocumentList/KeyItemPairDto'

const MimeTypeToMediaTypeMap = [
  [/^image\/[^/]+/, 'image'],
  [/^application\/pdf$/, 'iframe']
]

const resolveMediaTypeByMimeType = function(mimeType) {
  for (const mimeTypeToMediaTypeMap of MimeTypeToMediaTypeMap) {
    const mimeTypeRegularExpression = mimeTypeToMediaTypeMap[0]
    const mediaType = mimeTypeToMediaTypeMap[1]

    if (mimeType.match(mimeTypeRegularExpression)) {
      return mediaType
    }
  }

  return 'download'
}

/**
 * @member {Promise<VisitorDocumentList>|VisitorDocumentList} visitorDocumentList
 * @member {Promise<Array<KeyItemPairDto>>|Array<KeyItemPairDto>} keyItemPairs
 */
export default class VisitorDocumentListKeyItemPairHydrator {
  visitorDocumentList = {}
  keyItemPairs = []

  /**
   * @param {Promise<VisitorDocumentList>|VisitorDocumentList} visitorDocumentList
   * @return VisitorDocumentListKeyItemPairHydrator
   */
  setVisitorDocumentList(visitorDocumentList) {
    this.visitorDocumentList = visitorDocumentList

    return this
  }

  /**
   * @param {Promise<Array<KeyItemPairDto>>|Array<KeyItemPairDto>} keyItemPairs
   * @return VisitorDocumentListKeyItemPairHydrator
   */
  setKeyItemPairs(keyItemPairs) {
    this.keyItemPairs = keyItemPairs

    return this
  }

  /**
   * @return {Promise<VisitorDocumentList>}
   */
  async hydrateVisitorDocumentList() {
    const visitorDocumentList = await this.visitorDocumentList

    if (!visitorDocumentList) {
      throw new Error('No visitorDocumentList set')
    }

    const keyItemPairs = await this.keyItemPairs

    if (!keyItemPairs) {
      throw new Error('No keyItemPairs set')
    }

    for (const owner of Object.keys(visitorDocumentList)) {
      delete visitorDocumentList[owner]
    }
    for (const keyItemPair of keyItemPairs) {
      const owner = keyItemPair.key.owner
      const type = keyItemPair.key.type
      visitorDocumentList[owner] = visitorDocumentList[owner] ?? {}
      visitorDocumentList[owner][type] = visitorDocumentList[owner][type] ?? []
      visitorDocumentList[owner][type].push(new VisitorDocumentListItem(keyItemPair.item))
    }

    return visitorDocumentList
  }

  /**
   * @return {Promise<Array<KeyItemPairDto>>}
   */
  async hydrateKeyItemPairs() {
    const visitorDocumentList = await this.visitorDocumentList

    if (!visitorDocumentList) {
      throw new Error('No visitorDocumentList set')
    }

    const keyItemPairs = await this.keyItemPairs

    if (!keyItemPairs) {
      throw new Error('No keyItemPairs set')
    }

    while (keyItemPairs.length > 0) {
      keyItemPairs.pop()
    }

    for (const owner in visitorDocumentList) {
      const typeItemsMap = visitorDocumentList[owner]

      if (!typeItemsMap) {
        continue
      }

      if (!(typeItemsMap instanceof Object)) {
        throw new Error('Unexpected visitorDocumentList format returned')
      }

      for (const type in typeItemsMap) {
        const items = typeItemsMap[type]

        if (!items) {
          continue
        }

        if (!(items instanceof Array)) {
          throw new Error('Unexpected visitor document list items format')
        }

        for (const item of items) {
          if (item.mimeType && !item.mediaType) {
            item.mediaType = resolveMediaTypeByMimeType(item.mimeType)
          }

          keyItemPairs.push(new KeyItemPairDto({
            item: new VisitorDocumentListItem(item),
            key: new KeyDto({ owner, type })
          }))
        }
      }
    }

    return keyItemPairs
  }
}
